import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { API_URL } from '../api'; // Usamos API_URL directa, sin fetchWithAuth
import { Check, X, Zap, RotateCcw, ArrowLeft, Camera, Lock, LogOut } from 'react-feather';
import { useNavigate } from 'react-router-dom';

const QRScanner = () => {
  const navigate = useNavigate();

  // --- ESTADO: SESIÓN DEL GUARDIA ---
  const [activeSession, setActiveSession] = useState(null); // { id, name, pin }
  const [pinInput, setPinInput] = useState('');
  const [loadingPin, setLoadingPin] = useState(false);
  const [errorPin, setErrorPin] = useState('');

  // --- ESTADO: SCANNER ---
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanResult, setScanResult] = useState(null);
  const [history, setHistory] = useState([]);
  
  const videoRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());
  const beepRef = useRef(null);

  // 1. CARGAR SESIÓN AL INICIAR
  useEffect(() => {
    const savedSession = localStorage.getItem('guard_session');
    if (savedSession) {
      setActiveSession(JSON.parse(savedSession));
    }
    
    // Configurar Audio
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) beepRef.current = new AudioContext();
  }, []);

  const playBeep = (type) => {
    if (!beepRef.current) return;
    const ctx = beepRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    if (type === 'success') {
      osc.frequency.value = 800;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } else {
      osc.frequency.value = 300;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    }
  };

  // --- LÓGICA: LOGIN CON PIN ---
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pinInput.length !== 6) return;
    
    setLoadingPin(true);
    setErrorPin('');

    try {
      const res = await fetch(`${API_URL}/guard/validate-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput })
      });
      
      const data = await res.json();

      if (res.ok) {
        // Guardamos la sesión con el PIN para usarlo en cada escaneo
        const sessionData = { id: data.id, name: data.name, pin: pinInput };
        localStorage.setItem('guard_session', JSON.stringify(sessionData));
        setActiveSession(sessionData);
        setPinInput('');
      } else {
        setErrorPin('PIN incorrecto o evento no encontrado');
      }
    } catch (err) {
      setErrorPin('Error de conexión con el servidor');
    } finally {
      setLoadingPin(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("¿Cerrar sesión del evento?")) {
        stopScanning();
        localStorage.removeItem('guard_session');
        setActiveSession(null);
        setHistory([]);
    }
  };

  // --- LÓGICA: SCANNER ---
  const startScanning = async () => {
    if (!activeSession) return; // Solo escanea si hay sesión

    setScanStatus('scanning');
    setScanResult(null);
    try {
      // Reiniciamos el lector antes de iniciar para evitar conflictos
      codeReader.current.reset();
      
      await codeReader.current.decodeFromConstraints(
        { video: { facingMode: "environment" } },
        videoRef.current,
        (result) => { if (result) handleScan(result.getText()); }
      );
    } catch (err) {
      console.error("Error cámara:", err);
      setScanStatus('error');
      setScanResult({ message: 'Error de cámara' });
    }
  };

  const stopScanning = () => {
    codeReader.current.reset();
    setScanStatus('idle');
  };

  const handleScan = async (text) => {
    stopScanning(); 
    setScanStatus('processing');

    try {
      const qrData = JSON.parse(text);
      
      // Validación básica antes de enviar
      if (!qrData.event_id) throw new Error("QR inválido");

      // PETICIÓN AL NUEVO ENDPOINT (Sin Auth header, enviando PIN en el body)
      const response = await fetch(`${API_URL}/guard/scan-with-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ 
            qr_data: qrData, 
            pin: activeSession.pin 
        })
      }).then(r => r.json());

      console.log("RESPUESTA BACKEND:", response);

      const resultData = {
        status: response.validation_result,
        user_name: response.user ? response.user.name : 'Desconocido',
        event_name: response.event ? response.event.name : 'Evento Desconocido',
        message: getMessage(response.validation_result),
        timestamp: new Date().toLocaleTimeString('es-PE', { 
            timeZone: 'America/Lima', hour: '2-digit', minute: '2-digit', hour12: false 
        })
      };

      setScanResult(resultData);
      
      const isSuccess = response.validation_result === 'valid';
      setScanStatus(isSuccess ? 'success' : 'error');
      playBeep(isSuccess ? 'success' : 'error');
      
      setHistory(prev => [resultData, ...prev].slice(0, 15));

    } catch (err) {
      console.error(err);
      setScanStatus('error');
      setScanResult({ status: 'error', message: 'QR ILEGIBLE / ERROR', user_name: '---' });
      playBeep('error');
    }

    setTimeout(() => startScanning(), 2500);
  };

  const getMessage = (status) => {
    const messages = {
      'valid': 'ACCESO APROBADO',
      'already_used': 'YA INGRESÓ',
      'expired': 'TICKET VENCIDO',
      'invalid': 'TICKET FALSO',
      'wrong_event': 'OTRO EVENTO'
    };
    return messages[status] || 'ERROR';
  };

  // Iniciar cámara cuando cargue la sesión
  useEffect(() => {
    if (activeSession) {
        startScanning();
    }
    return () => stopScanning();
    // eslint-disable-next-line
  }, [activeSession]);


  // =========================================================
  // RENDER 1: PANTALLA DE PIN (LOGIN)
  // =========================================================
  if (!activeSession) {
    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 font-sans">
            <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-3xl p-8 text-center relative overflow-hidden">
                {/* Fondo decorativo sutil */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-blue-500"></div>
                
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-white border border-white/10">
                    <Lock size={28} />
                </div>
                
                <h1 className="text-2xl font-bold text-white mb-2">Modo Guardia</h1>
                <p className="text-white/40 mb-8 text-sm">Ingresa el PIN de 6 dígitos del evento</p>
                
                <form onSubmit={handlePinSubmit} className="space-y-6">
                    <input 
                        type="text" 
                        maxLength={6}
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="000 000"
                        className="w-full bg-black border border-white/20 rounded-2xl py-4 text-center text-4xl font-mono tracking-[0.3em] text-white focus:border-white focus:outline-none placeholder-white/10 transition-colors"
                        autoFocus
                    />
                    
                    {errorPin && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                             <p className="text-red-400 text-xs font-bold uppercase flex items-center justify-center gap-2">
                                <X size={14} /> {errorPin}
                             </p>
                        </div>
                    )}
                    
                    <button 
                        type="submit" 
                        disabled={loadingPin || pinInput.length < 6}
                        className="w-full bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-sm"
                    >
                        {loadingPin ? 'Verificando...' : 'Iniciar Escáner'}
                    </button>
                </form>
                
                <button onClick={() => navigate('/')} className="mt-8 text-xs text-white/30 hover:text-white transition-colors flex items-center justify-center gap-1 mx-auto">
                    <ArrowLeft size={12} /> Volver al inicio
                </button>
            </div>
        </div>
    );
  }

  // =========================================================
  // RENDER 2: SCANNER (TU CÓDIGO ORIGINAL CON PEQUEÑOS AJUSTES)
  // =========================================================
  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans flex items-center justify-center p-4 md:p-8">
      
      <div className="w-full max-w-5xl bg-[#111] rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col md:flex-row h-[85vh] md:h-[600px]">
        
        {/* COLUMNA IZQUIERDA: CÁMARA */}
        <div className="w-full md:w-1/2 relative bg-black flex flex-col h-[50%] md:h-full border-b md:border-b-0 md:border-r border-white/10">
          
          <div className="absolute top-0 left-0 w-full z-20 p-4 flex justify-between items-start bg-gradient-to-b from-black/80 to-transparent">
             {/* CAMBIO: Botón para salir/logout */}
             <button onClick={handleLogout} className="px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-full border border-white/10 hover:bg-red-500/20 hover:border-red-500/50 hover:text-red-400 transition-colors flex items-center gap-2">
                <LogOut size={14} />
                <span className="text-xs font-bold hidden md:inline">SALIR</span>
             </button>
          </div>

          {/* VIDEO ELEMENT */}
          <div className="relative w-full h-full overflow-hidden">
            <video
                ref={videoRef}
                className="w-full h-full object-cover" 
                muted
                playsInline
            />

            {/* OVERLAY: Nombre del evento activo (Para seguridad visual) */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center z-20 pointer-events-none">
                <div className="bg-black/60 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/10 text-xs font-bold text-white/80 uppercase tracking-wider">
                    {activeSession.name}
                </div>
            </div>

            {/* OVERLAYS (Scan Line) */}
            {scanStatus === 'scanning' && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-48 h-48 md:w-64 md:h-64 border border-white/20 rounded-2xl relative">
                        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-green-500 rounded-tl-lg" />
                        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-green-500 rounded-tr-lg" />
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-green-500 rounded-bl-lg" />
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-green-500 rounded-br-lg" />
                        <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.8)] animate-scan-laser" />
                    </div>
                </div>
            )}

            {/* OVERLAYS (Resultados) */}
            {(scanStatus === 'success' || scanStatus === 'error') && scanResult && (
                <div className={`absolute inset-0 flex flex-col items-center justify-center backdrop-blur-md transition-all duration-300 z-10 ${
                    scanStatus === 'success' ? 'bg-green-600/95' : 'bg-red-600/95'
                }`}>
                    <div className="bg-white rounded-full p-4 shadow-xl mb-4 scale-100 animate-bounce-short">
                        {scanStatus === 'success' ? <Check size={40} className="text-green-600" /> : <X size={40} className="text-red-600" />}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight text-center px-4 mb-1">
                        {scanResult.message}
                    </h2>
                    
                    <p className="text-white text-xl font-bold mb-1">{scanResult.user_name}</p>
                    <p className="text-white/70 text-sm font-medium mb-6 uppercase tracking-wider">{scanResult.event_name}</p>

                    <div className="w-full bg-black/20 h-1 absolute bottom-0">
                        <div className="h-full bg-white/50 animate-progress-bar origin-left" />
                    </div>
                </div>
            )}
            
            {scanStatus === 'processing' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-10">
                    <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA: HISTORIAL E INFO */}
        <div className="w-full md:w-1/2 bg-[#111] flex flex-col h-[50%] md:h-full relative z-0">
            <div className="p-6 border-b border-white/5 bg-[#161616]">
                <h1 className="text-xl font-bold flex items-center gap-3">
                    <Zap className="text-yellow-400" size={20} />
                    Control de Acceso
                </h1>
                <p className="text-white/40 text-sm mt-1">
                    Evento: <span className="text-white">{activeSession.name}</span>
                </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 custom-scrollbar">
                <div className="flex items-center justify-between mb-2 px-1">
                    <h3 className="text-xs font-bold text-white/30 uppercase tracking-widest">Actividad Reciente</h3>
                    <button onClick={() => setHistory([])} className="text-white/30 hover:text-white transition-colors p-1">
                        <RotateCcw size={12} />
                    </button>
                </div>

                {history.length === 0 ? (
                    <div className="h-40 flex flex-col items-center justify-center text-white/20 border-2 border-dashed border-white/5 rounded-xl">
                        <Camera size={24} className="mb-2 opacity-50" />
                        <span className="text-sm">Listo para escanear</span>
                    </div>
                ) : (
                    history.map((item, idx) => (
                        <div key={idx} className="group flex items-center justify-between p-4 bg-[#1a1a1a] hover:bg-[#222] rounded-xl border border-white/5 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    item.status === 'valid' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                                }`}>
                                    {item.status === 'valid' ? <Check size={18} /> : <X size={18} />}
                                </div>
                                <div>
                                    <p className="font-bold text-sm text-white">{item.user_name}</p>
                                    <p className={`text-xs font-medium ${item.status === 'valid' ? 'text-green-400' : 'text-red-400'}`}>
                                        {item.message}
                                    </p>
                                </div>
                            </div>
                            <span className="text-xs font-mono text-white/30 bg-black/30 px-2 py-1 rounded">
                                {item.timestamp}
                            </span>
                        </div>
                    ))
                )}
            </div>
            
            <div className="p-4 bg-[#0A0A0A] border-t border-white/5 flex justify-between text-xs text-white/40 font-mono">
                <span>PIN DE ACCESO ACTIVO</span>
                <span>V3.0.0</span>
            </div>
        </div>

      </div>

      <style>{`
        @keyframes scan-laser { 0% { top: 10%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 90%; opacity: 0; } }
        .animate-scan-laser { animation: scan-laser 2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
        @keyframes progress-bar { 0% { transform: scaleX(0); } 100% { transform: scaleX(1); } }
        .animate-progress-bar { animation: progress-bar 2.5s linear forwards; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.2); }
      `}</style>
    </div>
  );
};

export default QRScanner;